import { CdkDragDrop, copyArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbDialogService, NbToastRef, NbToastrService } from '@nebular/theme';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { AssessmentSettingService } from 'app/@core/backend/common/services/CalorieFriend/assessment-settings.service';
import { UserStore } from 'app/@core/stores/user.store';
import {cloneDeep} from 'lodash';
import { AssessmentFormSettingsComponent } from './assessmentFormSettings.component';
import { AssessmentDescriptiveValues } from '../PatientSheetView/Assessments/DescriptiveValues';
import { defaultFormValues } from 'app/@core/utils/defaultFormValues';
import { finalize } from 'rxjs/operators';
import { UsersService } from 'app/@core/backend/common/services/users.service';

@Component({
  selector: 'app-assessment-setting',
  templateUrl: './assessmentSetting.component.html',
  styleUrls: ['./assessmentSetting.component.scss'],
})
export class AssessmentSettingComponent implements OnInit {
  public form: FormGroup;
  public daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  public primaryColor;
  public secondaryColor;
  public previewForm: boolean = false;
  public isLoading: boolean = false;
  options: FormlyFormOptions = {
    formState: {
      customData: {
        isImperial: true,
        // Add more custom data as needed
      }
    }
  };

  fields = [
    { type: 'nbInput', name: 'Text Input', templateOptions: { label: 'Label', placeholder: 'Placeholder', required: false } },
    { type: 'nbInputNumber', name: 'Number Input', templateOptions: { label: 'Label', placeholder: 'Placeholder', required: false } },
    { type: 'nbCheckbox', name: 'Checkbox', templateOptions: { label: 'Label', required: false } },
    { type: 'nbRadio', name: 'Radio', templateOptions: { label: 'Label', options: [{ value: 'Option 1', label: 'Option 1' }, { value: 'Option 2', label: 'Option 2' }], required: false } },
    { type: 'nbSelect', name: 'Dropdown', templateOptions: { label: 'Label', options: [{ value: 'Option 1', label: 'Option 1' }, { value: 'Option 2', label: 'Option 2' }], required: false } },
    { type: 'nbTextarea', name: 'Textarea', templateOptions: { label: 'Label', placeholder: 'Placeholder', required: false } },
    // { type: 'nbWeight', name: 'Weight', templateOptions: { label: 'Label', placeholder: 'Placeholder', required: false, isImperial: false } },
    // add more field types as needed
  ];

  default = defaultFormValues;

  formStructureFields: FormlyFieldConfig[] = [ ];
  model={};
  constructor(private assessmentSettingService: AssessmentSettingService,
    private dialogService: NbDialogService,
    private fb: FormBuilder,
    private toasterService: NbToastrService,
    private userService: UsersService,
  ) {
    this.form = this.fb.group({
      id: [null],
      uploadProgressImages: [true],
      assessmentFrequency: [1],
      sendByEmail: [true],
      sendByMessage: [false],
      messageText: [`<h1 style='font-size: 24px; margin: 0;'>Hello {ClientName},</h1><br />
      <p style='margin: 0;'>
          In order to assist you in achieving your goals, we kindly request that you complete a quick self-assessment form on how things went this week. Filling out the form should take you less than a minute, and over time, it will provide crucial information on how to optimize and personalize your plan.
      </p>`],
      daysOfWeek: [['Sunday']],
      questionsToAsk: [null],
      customQuestion: [null],
    });
  }

  ngOnInit(): void {
    this.getAssessmentSettings();
  }

  public getAssessmentSettings(): void {
    this.isLoading = true;
    this.assessmentSettingService.getAssessmentSettings()
    .pipe(finalize(() => this.isLoading = false))
    .subscribe((data) => {
        if(data)
        {this.form.patchValue({
          ...data,
          messageText: data?.messageText || `<h1 style='font-size: 24px; margin: 0;'>Hello {ClientName},</h1><br />
          <p style='margin: 0;'>
              In order to assist you in achieving your goals, we kindly request that you complete a quick self-assessment form on how things went this week. Filling out the form should take you less than a minute, and over time, it will provide crucial information on how to optimize and personalize your plan.
          </p>`,
          daysOfWeek: JSON.parse(data?.daysOfWeek || '["Sunday"]'),
        });
        if(data?.customQuestion) {
          this.formStructureFields = JSON.parse(data.customQuestion);
        }
      }else {
          this.formStructureFields = this.default;
        }
    }, error => {
      this.formStructureFields = this.default;
    });
  }

  public get defaultFields(): FormlyFieldConfig[] {
    let obj = {};
    this.formStructureFields.forEach((field) => {
      obj[field.key.toString()] = true;
    });
    return this.default.filter((field) => !obj[field.key.toString()]);
  }

  public onSubmit(): void {
    if (this.form.valid) {
      this.isLoading = true;
      // this.formStructureFields = this.formStructureFields.map((field,index) => {
      //   field.key = `${field.type}__${Date.now()}__${index}`;
      //   return field;
      // });
      this.assessmentSettingService.updateAssessmentSettings({...this.form.value, daysOfWeek: JSON.stringify(this.form.value.daysOfWeek), customQuestion: JSON.stringify(this.formStructureFields)})
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(() => {
        this.toasterService.success('','Assessment settings updated successfully');
      }, error => {
        this.toasterService.danger('','Error updating Assessment settings');
      });
    }
  }

  public onDaySelectChange(day: string): void {
    const days = this.form.get('daysOfWeek').value || [];
    if (days.includes(day)) {
      const index = days.indexOf(day);
      if (index >= 0) {
        days.splice(index, 1);
      }
    } else {
      days.push(day);
    }
    this.form.get('daysOfWeek').setValue(days);
  }

  drop(event: CdkDragDrop<FormlyFieldConfig[]>) {
    if (event.previousContainer === event.container) {
      console.log("Moving");
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      console.log("Copying...", event.container.data, event.previousContainer.data[event.previousIndex].type);
      let tempData = cloneDeep(event.previousContainer.data) as FormlyFieldConfig[];
      if(!(tempData[event.previousIndex] as any).defaultField) tempData[event.previousIndex].key = `${event.previousContainer.data[event.previousIndex].type}__${Date.now()}__${event.container.data.length + 1}`;
      copyArrayItem(tempData,
                    event.container.data,
                    event.previousIndex,
                    event.currentIndex);
    }
  }
  addDuplicate(field: FormlyFieldConfig): void {
    let temp = cloneDeep(field);
    temp.key = `${field.type}_${this.formStructureFields.length + 1}`;
    this.formStructureFields.push(temp);
  }
  deleteField(index: number): void {
    this.formStructureFields.splice(index, 1);
  }

  editField(index: number): void {
    this.dialogService.open(AssessmentFormSettingsComponent, {
      context: {
        field: cloneDeep(this.formStructureFields[index]),
      },
    }).onClose.subscribe((data) => {
      if (data) {
        this.formStructureFields[index] = data;
      }
    });
  }

}
